import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Clock, Trophy, Zap, ArrowLeft, Sparkles, Lock, Crown, Ticket, Hash } from "lucide-react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { formatEuros } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function ChallengeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [myCodes, setMyCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/challenges/public/${id}`);
        setChallenge(data);
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/");
      }
    })();
  }, [id, navigate]);

  // Fetch the connected user's codes so we know whether they already own a
  // compatible code (avoids the wrong-1€-Stripe redirect on Golden Quizz).
  useEffect(() => {
    if (!user || user === false) {
      setMyCodes([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/play/me/codes");
        setMyCodes(data?.codes || []);
      } catch {
        setMyCodes([]);
      }
    })();
  }, [user]);

  const isAuthed = !!user && user !== false;
  const category = challenge?.category || "standard";
  const isGolden = category === "golden";
  // Compatible AVAILABLE codes (not yet bound to a challenge)
  const compatibleAvailable = myCodes.filter(
    (c) => c.status === "available" && (c.code_type || "standard") === category,
  );
  // Already reserved on THIS challenge — `my_reserved_code` is the canonical
  // signal coming from the backend (works even when /play/me/codes hasn't
  // refreshed yet).
  const reservedHere =
    (challenge?.my_reserved_code && { code: challenge.my_reserved_code }) ||
    myCodes.find(
      (c) => c.status === "reserved" && String(c.challenge_id || "") === String(id),
    );

  // --- Actions ---
  const goLogin = (intent) => {
    try {
      sessionStorage.setItem("otra_buy_intent", intent);
    } catch (_e) {
      // ignore storage errors
    }
    toast.info("Connecte-toi pour participer");
    navigate("/login?next=buy");
  };

  // Standard challenges only: direct 1 € Stripe checkout that gives back a
  // standard code already linked to this challenge. NEVER used for golden.
  const payStandard = async () => {
    if (isGolden) return; // safety net
    if (!isAuthed) {
      goLogin(`challenge:${id}`);
      return;
    }
    if (!ageConfirmed) {
      toast.error(
        "Confirme d'abord avoir 18 ans ou plus et accepter les CGU / CGV.",
      );
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout/session", {
        challenge_id: id,
        origin_url: window.location.origin,
        age_confirmed: true,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  // v2.39 — Free-entry flow: mint a comp code via /join-free without Stripe.
  // Refresh myCodes so the CTA flips to "Place réservée" / "Participer".
  const bookFreeChallenge = async () => {
    if (!isAuthed) {
      goLogin(`challenge:${id}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/play/challenges/${id}/join-free`);
      const codeStr = data?.code;
      const reused = !!data?.reused;
      toast.success(
        reused ? "Place déjà réservée." : "Ta place est réservée gratuitement.",
      );
      // Refresh user's codes so the CTA flips to "Place réservée".
      try {
        const res = await api.get("/play/me/codes");
        setMyCodes(res?.data?.codes || []);
      } catch {
        // ignore — the toast is enough to acknowledge success
      }
      // If challenge is already open, jump straight into the play flow.
      if (open && codeStr) {
        navigate(`/play?code=${encodeURIComponent(codeStr)}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Pre-register an existing AVAILABLE code on this scheduled challenge.
  const preRegisterCode = async (code) => {
    setBusy(true);
    try {
      await api.post("/codes/pre-register", { code, challenge_id: id });
      toast.success("Place réservée !");
      // Refresh user's codes + show updated state
      try {
        const { data } = await api.get("/play/me/codes");
        setMyCodes(data?.codes || []);
      } catch {
        // ignore
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Launch the play page with an existing code on an OPEN challenge.
  const playWithCode = (code) => {
    navigate(`/play?code=${encodeURIComponent(code)}`);
  };

  if (!challenge) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-16 text-white/50 font-mono-stat uppercase">
          Chargement...
        </div>
      </Layout>
    );
  }

  const finalized = challenge.status === "finalized" || challenge.closed === true;
  const pendingFinal = challenge.status === "pending_finalization";
  const scheduled = challenge.status === "scheduled";
  const open = challenge.status === "open";

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <button
          onClick={() => navigate("/")}
          data-testid="challenge-back-button"
          className="flex items-center gap-2 text-white/60 hover:text-cyan-300 mb-6 text-sm font-mono-stat uppercase tracking-widest"
        >
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <div
          className={`rounded-3xl p-5 sm:p-8 scanlines relative ${
            isGolden
              ? "border border-amber-400/50 bg-gradient-to-b from-amber-400/10 via-amber-400/0 to-black/40 shadow-[0_0_45px_rgba(251,191,36,0.2)]"
              : "panel"
          }`}
        >
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-mono-stat text-cyan-300">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                {challenge.type === "series" ? "Série" : "Quiz unique"}
              </span>
              {isGolden && (
                <span
                  data-testid="challenge-golden-badge"
                  className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/50 text-amber-200 text-[10px] uppercase tracking-[0.3em] font-display font-bold"
                >
                  <Crown className="w-3 h-3" />
                  Golden Quizz
                </span>
              )}
            </div>
            <span
              data-testid="challenge-status-badge"
              className={`text-[10px] uppercase tracking-[0.3em] font-mono-stat px-3 py-1 rounded-full ${
                open
                  ? "bg-emerald-400/15 text-emerald-300 border border-emerald-400/40"
                  : scheduled
                  ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/40"
                  : pendingFinal
                  ? "bg-orange-400/15 text-orange-300 border border-orange-400/40"
                  : "bg-amber-400/15 text-amber-300 border border-amber-400/40"
              }`}
            >
              {open
                ? "Ouvert"
                : scheduled
                ? "À venir"
                : pendingFinal
                ? "Finalisation"
                : "Terminé"}
            </span>
          </div>

          <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mb-3">
            {challenge.title}
          </h1>
          {challenge.description && (
            <p className="text-white/70 leading-relaxed mb-6">{challenge.description}</p>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-7">
            <Stat icon={<Sparkles className="w-4 h-4" />} label="Questions" value={challenge.total_questions} />
            <Stat icon={<Zap className="w-4 h-4" />} label="Quiz" value={challenge.quiz_count} />
            <Stat
              icon={<Clock className="w-4 h-4" />}
              label="Début"
              value={new Date(challenge.starts_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            />
            <Stat
              icon={<Clock className="w-4 h-4" />}
              label="Fin"
              value={new Date(challenge.ends_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            />
          </div>

          {challenge.reward_description && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 mb-4 text-sm text-white/70">
              {challenge.reward_description}
            </div>
          )}

          {/* v2.41 — Big qualification-reward block (only for qualifier
              challenges). Sits above the prize-pool block so the "place en
              finale à gagner" is at least as visible as a cash prize. */}
          {challenge.reward_type === "qualification" && challenge.qualification_config && (
            <div
              data-testid="challenge-qualification-reward"
              className="rounded-2xl border border-cyan-400/50 bg-gradient-to-br from-cyan-400/12 via-fuchsia-400/6 to-transparent p-5 sm:p-6 mb-7 shadow-[0_0_35px_rgba(34,211,238,0.18)]"
            >
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-12 h-12 rounded-xl bg-cyan-400/20 border border-cyan-400/50 flex items-center justify-center shrink-0">
                  <Crown className="w-6 h-6 text-cyan-200" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-mono-stat text-cyan-300 mb-1">
                    Qualification à gagner
                  </div>
                  {(() => {
                    const slots = Number(challenge.qualification_config.qualified_count) || 0;
                    const slotsLine =
                      slots === 1
                        ? "1 place pour la finale"
                        : slots > 1
                        ? `Top ${slots} qualifiés`
                        : "Qualification pour la finale";
                    return (
                      <div
                        data-testid="challenge-qualification-slots"
                        className="font-display font-black text-2xl sm:text-3xl leading-tight text-white"
                      >
                        {slotsLine}
                      </div>
                    );
                  })()}
                  {challenge.qualification_config.destination_challenge_title && (
                    <div className="mt-3 pt-3 border-t border-cyan-400/20">
                      <div className="text-[10px] uppercase tracking-widest font-mono-stat text-white/50 mb-1">
                        Finale de destination
                      </div>
                      <div
                        data-testid="challenge-qualification-destination"
                        className="font-display font-bold text-lg text-cyan-100"
                      >
                        {challenge.qualification_config.destination_challenge_title}
                      </div>
                      {Number(
                        challenge.qualification_config.destination_prize_pool_base,
                      ) > 0 && (
                        <div className="text-xs text-amber-300 font-mono-stat mt-1">
                          Cagnotte finale :{" "}
                          <span className="font-bold">
                            {formatEuros(
                              challenge.qualification_config.destination_prize_pool_base,
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {challenge.qualification_config.message && (
                    <p className="mt-3 text-sm text-white/70 italic">
                      « {challenge.qualification_config.message} »
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Show the cash prize block only when this challenge actually has
              money — a qualifier's reward IS the qualification block above. */}
          {(challenge.prize_pool_base > 0 || challenge.reward_type !== "qualification") && (
          <div
            data-testid="challenge-prize-pool"
            className={`rounded-2xl border p-5 sm:p-6 mb-7 ${
              isGolden
                ? "border-amber-400/60 bg-gradient-to-br from-amber-400/18 via-amber-400/6 to-transparent shadow-[0_0_45px_rgba(251,191,36,0.25)]"
                : "border-amber-400/40 bg-gradient-to-br from-amber-400/10 to-amber-500/5"
            }`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                    isGolden
                      ? "bg-amber-400/25 border border-amber-400/60"
                      : "bg-amber-400/15 border border-amber-400/40"
                  }`}
                >
                  {isGolden ? (
                    <Crown className="w-6 h-6 text-amber-300" />
                  ) : (
                    <Trophy className="w-6 h-6 text-amber-300" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-mono-stat text-amber-300">
                    {isGolden ? "Cagnotte Golden" : "Cagnotte totale"}
                  </div>
                  <div className="text-xs text-white/55 mt-0.5">
                    {challenge.prize_pool_base > 0
                      ? `${challenge.winners_count || 1} gagnant${
                          (challenge.winners_count || 1) > 1 ? "s" : ""
                        } récompensé${(challenge.winners_count || 1) > 1 ? "s" : ""}`
                      : "Aucune cagnotte définie sur ce défi"}
                  </div>
                </div>
              </div>
              <div className="text-right ml-auto">
                {challenge.current_prize_pool > 0 ? (
                  <div
                    data-testid="challenge-prize-pool-amount"
                    className={`font-display font-black leading-none ${
                      isGolden
                        ? "text-4xl sm:text-5xl text-amber-300 neon-text-secondary"
                        : "text-3xl sm:text-4xl text-amber-300 neon-text-secondary"
                    }`}
                  >
                    {formatEuros(challenge.current_prize_pool)}
                  </div>
                ) : (
                  <div
                    data-testid="challenge-prize-pool-empty"
                    className="font-display font-bold text-xs sm:text-sm text-amber-300/60"
                  >
                    Aucune cagnotte
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-widest font-mono-stat text-white/40 mt-1">
                  {challenge.participant_count} inscrit{(challenge.participant_count || 0) > 1 ? "s" : ""}
                </div>
              </div>
            </div>
          </div>
          )}

          {Array.isArray(challenge.prize_distribution) && challenge.prize_pool_base > 0 && (
            <div
              data-testid="challenge-distribution"
              className="rounded-2xl border border-amber-400/30 bg-black/30 p-5 mb-7"
            >
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-4 h-4 text-amber-300" />
                <span className="font-display font-bold uppercase tracking-widest text-sm">
                  Récompenses
                </span>
                <span className="text-[10px] uppercase tracking-widest font-mono-stat text-white/40 ml-auto">
                  Cagnotte totale · {formatEuros(challenge.current_prize_pool)}
                </span>
              </div>
              <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest text-white/35 font-mono-stat pb-1 border-b border-white/5 mb-2">
                <div className="col-span-2">Place</div>
                <div className="col-span-5">Pourcentage</div>
                <div className="col-span-5 text-right">Montant</div>
              </div>
              {challenge.prize_distribution.map((d) => {
                const label = d.rank === 1 ? "1er" : `${d.rank}e`;
                const accent =
                  d.rank === 1
                    ? "text-amber-300"
                    : d.rank === 2
                    ? "text-cyan-200"
                    : d.rank === 3
                    ? "text-fuchsia-300"
                    : "text-white/70";
                return (
                  <div
                    key={d.rank}
                    data-testid={`challenge-distribution-row-${d.rank}`}
                    className="grid grid-cols-12 py-1.5 items-center text-sm"
                  >
                    <div className={`col-span-2 font-display font-bold ${accent}`}>
                      {label}
                    </div>
                    <div className="col-span-5 font-mono-stat text-white/75">
                      {Number(d.percentage).toFixed(0)} %
                    </div>
                    <div
                      data-testid={`challenge-distribution-amount-${d.rank}`}
                      className={`col-span-5 text-right font-mono-stat font-bold ${accent}`}
                    >
                      {formatEuros(d.amount)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {finalized ? (
            <button
              data-testid="challenge-view-leaderboard-button"
              onClick={() => navigate(`/leaderboard/${id}`)}
              className="w-full flex items-center justify-center gap-3 px-6 py-5 rounded-2xl bg-amber-400 text-black font-display font-bold uppercase tracking-wider hover:bg-amber-300 active:scale-95 transition-all"
            >
              <Trophy className="w-5 h-5" />
              Voir le classement final
            </button>
          ) : pendingFinal ? (
            <div
              data-testid="challenge-pending-finalization"
              className="w-full px-6 py-5 rounded-2xl bg-orange-400/10 border border-orange-400/40 text-orange-200 text-center"
            >
              <div className="font-display font-bold uppercase tracking-wider mb-1">
                Défi terminé
              </div>
              <p className="text-sm text-white/70">
                Classement en cours de finalisation. Certaines sessions
                commencées avant la fermeture sont encore en cours.
              </p>
            </div>
          ) : (
            <CtaSection
              challenge={challenge}
              isGolden={isGolden}
              isAuthed={isAuthed}
              open={open}
              scheduled={scheduled}
              compatibleAvailable={compatibleAvailable}
              reservedHere={reservedHere}
              busy={busy}
              ageConfirmed={ageConfirmed}
              onAgeConfirmChange={setAgeConfirmed}
              onPayStandard={payStandard}
              onBookFree={bookFreeChallenge}
              onPreRegister={preRegisterCode}
              onPlayWithCode={playWithCode}
              onGoLogin={() => goLogin(`challenge:${id}`)}
              onGoBuy={() => navigate("/acheter")}
              onGoPlay={() => navigate("/play")}
              onGoQualifiers={() => navigate("/")}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}

function CtaSection({
  challenge,
  isGolden,
  isAuthed,
  open,
  scheduled,
  compatibleAvailable,
  reservedHere,
  busy,
  ageConfirmed,
  onAgeConfirmChange,
  onPayStandard,
  onBookFree,
  onPreRegister,
  onPlayWithCode,
  onGoLogin,
  onGoBuy,
  onGoPlay,
  onGoQualifiers,
}) {
  const isFree =
    challenge.entry_mode === "free" || Number(challenge.price_amount || 0) === 0;
  const isFinaleDest = challenge.access_mode === "qualified_only";
  const isQualifierSource = challenge.reward_type === "qualification";
  const hasCompletedSession = !!challenge.my_completed_session_id;

  // v2.46 — Quiz still open + user already played (any type of quiz):
  // no "Participer" CTA — show a "Déjà joué" label + a big
  // "Voir le classement provisoire" button. The qualifier variant below
  // adds the extra qualification note.
  if (open && hasCompletedSession) {
    return (
      <div className="space-y-3">
        <div
          data-testid="challenge-already-played-banner"
          className="rounded-2xl border border-cyan-400/40 bg-cyan-400/10 p-5 text-center"
        >
          <div className="text-cyan-200 font-display font-bold uppercase tracking-wider mb-1">
            Déjà joué
          </div>
          <p className="text-sm text-white/70">
            {isQualifierSource
              ? "Classement provisoire — les qualifications seront confirmées à la fermeture du quiz."
              : "Classement provisoire — les résultats seront figés à la fermeture du quiz."}
          </p>
        </div>
        <button
          data-testid="challenge-see-provisional-leaderboard-cta"
          onClick={() => (window.location.href = `/leaderboard/${challenge.id}`)}
          className="w-full flex items-center justify-center gap-3 px-6 py-5 rounded-2xl bg-cyan-400 hover:bg-cyan-300 text-black font-display font-bold uppercase tracking-wider active:scale-95 transition-all glow-primary"
        >
          <Trophy className="w-5 h-5" />
          Voir le classement provisoire
        </button>
      </div>
    );
  }

  // v2.41 — Single-shot qualifier: once played (even if quiz not yet open?
  // rare), no replay. Show a "Voir mon classement" CTA and a soft note.
  // Takes priority over reservedHere for qualifier quizzes because the
  // reservedHere path would otherwise offer to "Jouer maintenant" using the
  // code the user already consumed.
  if (isQualifierSource && hasCompletedSession) {
    return (
      <div className="space-y-3">
        <div
          data-testid="challenge-qualifier-completed"
          className="rounded-2xl border border-cyan-400/40 bg-cyan-400/10 p-5 text-center"
        >
          <div className="text-cyan-200 font-display font-bold uppercase tracking-wider mb-1">
            Participation terminée
          </div>
          <p className="text-sm text-white/70">
            Classement provisoire — les qualifications seront confirmées à la
            fermeture du quiz.
          </p>
        </div>
        <button
          data-testid="challenge-see-leaderboard-cta"
          onClick={() => (window.location.href = `/leaderboard/${challenge.id}`)}
          className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/15 text-white font-display font-bold uppercase tracking-wider transition-all"
        >
          Voir le classement
        </button>
      </div>
    );
  }
  // Already reserved on THIS challenge — show different CTA based on whether
  // the challenge is currently OPEN ("Jouer maintenant") or still scheduled.
  if (reservedHere) {
    const isFinaleCode = isFinaleDest || String(reservedHere.code || "").startsWith("QUAL-");
    if (open) {
      return (
        <div className="space-y-3">
          <button
            data-testid="challenge-play-reserved-now"
            onClick={() => onPlayWithCode(reservedHere.code)}
            className={`w-full flex items-center justify-center gap-3 px-6 py-5 rounded-2xl font-display font-bold uppercase tracking-wider active:scale-95 transition-all ${
              isFinaleCode || isGolden
                ? "bg-amber-400 hover:bg-amber-300 text-black shadow-[0_0_35px_rgba(251,191,36,0.45)]"
                : "bg-cyan-400 hover:bg-cyan-300 text-black glow-primary"
            }`}
          >
            {isFinaleCode || isGolden ? <Crown className="w-5 h-5" /> : <Ticket className="w-5 h-5" />}
            {isFinaleCode
              ? "Accéder à ma finale"
              : "Tu es inscrit — Jouer maintenant"}
          </button>
          <p className="text-xs text-white/45 text-center font-mono-stat">
            {isFinaleCode ? "Qualification " : "Code "}
            <span className="text-white">{reservedHere.code}</span> · ta place
            est garantie
          </p>
        </div>
      );
    }
    return (
      <div
        data-testid="challenge-already-reserved"
        className="w-full px-6 py-5 rounded-2xl bg-cyan-400/10 border border-cyan-400/40 text-cyan-100 text-center"
      >
        <div className="font-display font-bold uppercase tracking-wider mb-1 text-cyan-200">
          {isFinaleCode ? "Tu es qualifié" : "Tu es inscrit"}
        </div>
        <p className="text-sm text-white/70">
          {isFinaleCode ? "Qualification " : "Ton code "}
          <span className="font-mono-stat text-white">{reservedHere.code}</span>{" "}
          est prête. Reviens ici quand le défi sera ouvert pour jouer.
        </p>
      </div>
    );
  }

  // Not logged in: surface a clear next step.
  if (!isAuthed) {
    return (
      <div className="space-y-3">
        <button
          data-testid="challenge-login-cta"
          onClick={onGoLogin}
          className="w-full flex items-center justify-center gap-3 px-6 py-5 rounded-2xl bg-cyan-400 text-black font-display font-bold uppercase tracking-wider hover:bg-cyan-300 active:scale-95 transition-all glow-primary"
        >
          <Lock className="w-5 h-5" />
          Se connecter pour participer
        </button>
        {isGolden && (
          <p className="text-xs text-amber-300/80 text-center font-mono-stat uppercase tracking-widest">
            <Crown className="inline w-3 h-3 mr-1" />
            Golden Quizz — Golden Code requis
          </p>
        )}
      </div>
    );
  }

  // v2.39 — Finale (access_mode='qualified_only') and user is NOT qualified:
  // the reservedHere branch above catches qualified users via their QUAL-* code.
  // Here we surface a locked banner with a clear next step.
  if (isFinaleDest) {
    return (
      <div className="space-y-3">
        <div
          data-testid="challenge-finale-locked-banner"
          className="rounded-2xl border border-amber-400/50 bg-amber-400/10 p-5 text-center"
        >
          <div className="flex items-center justify-center gap-2 text-amber-200 font-display font-bold uppercase tracking-wider mb-1">
            <Lock className="w-4 h-4" />
            Réservé aux qualifiés
          </div>
          <p className="text-xs text-white/70">
            Participe aux quiz qualificatifs pour gagner ta place dans cette
            finale.
          </p>
        </div>
        <button
          data-testid="challenge-see-qualifiers-cta"
          onClick={onGoQualifiers}
          className="w-full flex items-center justify-center gap-3 px-6 py-5 rounded-2xl bg-cyan-400 text-black font-display font-bold uppercase tracking-wider hover:bg-cyan-300 active:scale-95 transition-all glow-primary"
        >
          <Sparkles className="w-5 h-5" />
          Voir les quiz qualificatifs
        </button>
      </div>
    );
  }

  // v2.39 — Free-entry flow: creates a comp code without Stripe.
  if (isFree && !isGolden) {
    return (
      <div className="space-y-3">
        <button
          data-testid="challenge-book-free-cta"
          onClick={onBookFree}
          disabled={busy}
          className="w-full flex items-center justify-center gap-3 px-6 py-5 rounded-2xl bg-fuchsia-500 text-white font-display font-bold uppercase tracking-wider hover:bg-fuchsia-400 active:scale-95 disabled:opacity-50 transition-all shadow-[0_0_25px_rgba(217,70,239,0.35)]"
        >
          <Ticket className="w-5 h-5" />
          {busy
            ? "…"
            : scheduled
            ? "Réserver gratuitement"
            : "Participer gratuitement"}
        </button>
        <p className="text-xs text-fuchsia-200/70 text-center font-mono-stat">
          {isQualifierSource
            ? "Participation gratuite — les meilleurs seront qualifiés pour la finale"
            : "Participation gratuite — aucune carte demandée"}
        </p>
      </div>
    );
  }

  // Golden challenge flow
  if (isGolden) {
    if (compatibleAvailable.length > 0) {
      const first = compatibleAvailable[0];
      return (
        <div className="space-y-3">
          <button
            data-testid="challenge-use-golden-code"
            onClick={() =>
              scheduled ? onPreRegister(first.code) : onPlayWithCode(first.code)
            }
            disabled={busy}
            className="w-full flex items-center justify-center gap-3 px-6 py-5 rounded-2xl bg-amber-400 text-black font-display font-bold uppercase tracking-wider hover:bg-amber-300 active:scale-95 disabled:opacity-50 transition-all shadow-[0_0_35px_rgba(251,191,36,0.45)]"
          >
            <Crown className="w-5 h-5" />
            {busy
              ? "…"
              : scheduled
              ? `Réserver ma place avec ${first.code}`
              : `Jouer avec ${first.code}`}
          </button>
          <p className="text-xs text-amber-200/70 text-center font-mono-stat">
            Tu as {compatibleAvailable.length} Golden Code{compatibleAvailable.length > 1 ? "s" : ""}{" "}
            disponible{compatibleAvailable.length > 1 ? "s" : ""}.
          </p>
        </div>
      );
    }
    // Golden + no code → never Stripe 1 €. Push to /acheter (pack50) or saisir un code.
    return (
      <div className="space-y-3">
        <div
          data-testid="challenge-golden-required-banner"
          className="rounded-2xl border border-amber-400/50 bg-amber-400/10 p-4 text-center"
        >
          <div className="flex items-center justify-center gap-2 text-amber-200 font-display font-bold uppercase tracking-wider mb-1">
            <Crown className="w-4 h-4" />
            Golden Code requis
          </div>
          <p className="text-xs text-white/65">
            Ce défi exclusif n&apos;est accessible qu&apos;avec un Golden Code. Aucun
            paiement standard 1 € n&apos;est possible ici.
          </p>
        </div>
        <button
          data-testid="challenge-buy-golden-cta"
          onClick={onGoBuy}
          className="w-full flex items-center justify-center gap-3 px-6 py-5 rounded-2xl bg-amber-400 text-black font-display font-bold uppercase tracking-wider hover:bg-amber-300 active:scale-95 transition-all shadow-[0_0_35px_rgba(251,191,36,0.4)]"
        >
          <Ticket className="w-5 h-5" />
          Acheter un pack avec Golden Code
        </button>
        <button
          data-testid="challenge-have-code-cta"
          onClick={onGoPlay}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-white/5 border border-white/15 hover:border-amber-400/40 text-white/80 font-display font-bold uppercase tracking-wider transition-all"
        >
          <Hash className="w-4 h-4" />
          J&apos;ai un Golden Code
        </button>
      </div>
    );
  }

  // Standard challenge flow
  if (compatibleAvailable.length > 0) {
    const first = compatibleAvailable[0];
    return (
      <div className="space-y-3">
        <button
          data-testid="challenge-use-standard-code"
          onClick={() => (scheduled ? onPreRegister(first.code) : onPlayWithCode(first.code))}
          disabled={busy}
          className="w-full flex items-center justify-center gap-3 px-6 py-5 rounded-2xl bg-cyan-400 text-black font-display font-bold uppercase tracking-wider hover:bg-cyan-300 active:scale-95 disabled:opacity-50 transition-all glow-primary"
        >
          <Ticket className="w-5 h-5" />
          {busy
            ? "…"
            : scheduled
            ? `Réserver ma place avec ${first.code}`
            : `Jouer avec ${first.code}`}
        </button>
        <p className="text-xs text-white/40 text-center font-mono-stat">
          Tu as {compatibleAvailable.length} code{compatibleAvailable.length > 1 ? "s" : ""}{" "}
          standard{compatibleAvailable.length > 1 ? "s" : ""} disponible
          {compatibleAvailable.length > 1 ? "s" : ""}.
        </p>
      </div>
    );
  }

  // Standard + no compatible code → 1 € Stripe still valid (creates 1 code linked here)
  return (
    <div className="space-y-3">
      <label
        data-testid="challenge-age-confirm-wrap"
        htmlFor="challenge-age-confirm"
        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
          ageConfirmed
            ? "border-emerald-400/50 bg-emerald-400/5"
            : "border-white/15 bg-white/5 hover:border-cyan-400/40"
        }`}
      >
        <input
          id="challenge-age-confirm"
          data-testid="challenge-age-confirm-checkbox"
          type="checkbox"
          checked={ageConfirmed}
          onChange={(e) => onAgeConfirmChange(e.target.checked)}
          className="mt-1 w-4 h-4 accent-cyan-400 shrink-0 cursor-pointer"
        />
        <div className="text-xs text-white/75 leading-relaxed">
          Je confirme avoir{" "}
          <span className="text-white font-semibold">18 ans ou plus</span> et
          j&apos;accepte les{" "}
          <Link
            to="/cgu"
            target="_blank"
            rel="noopener"
            className="text-cyan-300 underline hover:text-cyan-200"
          >
            CGU
          </Link>{" "}
          et les{" "}
          <Link
            to="/cgv"
            target="_blank"
            rel="noopener"
            className="text-cyan-300 underline hover:text-cyan-200"
          >
            CGV
          </Link>
          .
        </div>
      </label>
      <button
        data-testid="challenge-pay-button"
        onClick={onPayStandard}
        disabled={busy || !ageConfirmed}
        title={
          !ageConfirmed
            ? "Confirme d'abord avoir 18 ans ou plus et accepter les CGU / CGV."
            : undefined
        }
        className="w-full flex items-center justify-center gap-3 px-6 py-5 rounded-2xl bg-cyan-400 text-black font-display font-bold uppercase tracking-wider hover:bg-cyan-300 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all glow-primary"
      >
        <Lock className="w-5 h-5" />
        {busy
          ? "Redirection..."
          : scheduled
          ? `Réserver ma place · ${formatEuros(challenge.price_amount)}`
          : `Participer · ${formatEuros(challenge.price_amount)}`}
      </button>
      <button
        data-testid="challenge-buy-pack-cta"
        onClick={onGoBuy}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-white/5 border border-white/15 hover:border-fuchsia-400/40 text-white/80 font-display font-bold uppercase tracking-wider transition-all text-sm"
      >
        <Sparkles className="w-4 h-4" />
        Voir les packs (5 € / 50 €)
      </button>
      <p className="text-xs text-white/40 text-center font-mono-stat">
        Paiement sécurisé via Stripe · Code personnel généré après confirmation
      </p>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="rounded-xl bg-black/40 border border-white/10 p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono-stat text-white/50 mb-1">
        {icon}
        {label}
      </div>
      <div className="font-display font-bold text-base truncate">{value}</div>
    </div>
  );
}
